import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Github } from 'lucide-react';
import { contactData } from '../data/personalData';

const OWNER = 'adityabharti83';
const REPOS = ['pune-transit-monitor', 'airbnb-market-analytics', 'Aditya-Bharati'];
const SHOW = 8;
const CACHE_KEY = 'gitlog:v1';
const CACHE_MS = 10 * 60 * 1000;

interface Commit {
  sha: string;
  message: string;
  repo: string;
  date: string;
  url: string;
}

type Status = 'idle' | 'loading' | 'ready' | 'empty' | 'error';

const timeAgo = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff)) return '';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
};

const fetchRepo = async (repo: string): Promise<Commit[]> => {
  const res = await fetch(`https://api.github.com/repos/${OWNER}/${repo}/commits?per_page=6`, {
    headers: { Accept: 'application/vnd.github+json' },
  });
  if (!res.ok) throw new Error(`${repo}: HTTP ${res.status}`);
  const data = await res.json();
  if (!Array.isArray(data)) return [];
  return data.map((c: any) => ({
    sha: String(c.sha).slice(0, 7),
    message: String(c.commit?.message ?? '').split('\n')[0],
    repo,
    date: c.commit?.author?.date || c.commit?.committer?.date || '',
    url: c.html_url,
  }));
};

const readCache = (): Commit[] | null => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { at, commits } = JSON.parse(raw);
    return Date.now() - at < CACHE_MS ? commits : null;
  } catch {
    return null;
  }
};

const writeCache = (commits: Commit[]) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), commits }));
  } catch {
    // storage unavailable — fine, just refetch next time
  }
};

export const GitLog: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '200px' });
  const [status, setStatus] = useState<Status>('idle');
  const [commits, setCommits] = useState<Commit[]>([]);
  const started = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // fetch once, the first time the section nears the viewport
  useEffect(() => {
    if (!inView || started.current) return;
    started.current = true;

    const cached = readCache();
    if (cached) {
      setCommits(cached);
      setStatus(cached.length ? 'ready' : 'empty');
      return;
    }

    setStatus('loading');
    Promise.allSettled(REPOS.map(fetchRepo)).then((results) => {
      if (!mounted.current) return;
      const ok = results.filter((r): r is PromiseFulfilledResult<Commit[]> => r.status === 'fulfilled');
      if (ok.length === 0) {
        setStatus('error');
        return;
      }
      const merged = ok
        .flatMap((r) => r.value)
        .filter((c) => c.date)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, SHOW);
      writeCache(merged);
      setCommits(merged);
      setStatus(merged.length ? 'ready' : 'empty');
    });
  }, [inView]);

  return (
    <section id="git-log" className="py-24 px-6 bg-background" ref={ref}>
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <p className="font-mono text-sm text-accent mb-2">$ git log --oneline --all</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-text mb-3">recent commits</h2>
          <p className="text-muted max-w-2xl">
            Pulled live from the GitHub API, so this list updates whenever I push.
          </p>
        </motion.div>

        <div className="rounded-lg border border-border bg-surface overflow-hidden">
          <div className="flex items-center gap-1.5 px-4 py-2.5 bg-raised border-b border-border">
            <span className="w-2.5 h-2.5 rounded-full bg-danger/60" />
            <span className="w-2.5 h-2.5 rounded-full bg-accent/60" />
            <span className="w-2.5 h-2.5 rounded-full bg-accent2/60" />
            <span className="ml-3 text-xs font-mono text-muted">git log</span>
          </div>

          <div className="p-4 sm:p-5 font-mono text-xs sm:text-sm">
            {(status === 'idle' || status === 'loading') && (
              <p className="text-muted">
                fetching commits
                <span className="inline-block w-2 h-4 bg-accent ml-1 align-middle animate-blink" />
              </p>
            )}

            {status === 'error' && (
              <p className="text-muted leading-relaxed">
                <span className="text-danger">fatal:</span> couldn't reach the GitHub API (rate limit or offline).{' '}
                <a
                  href={contactData.profiles.github}
                  target="_blank"
                  rel="noreferrer"
                  className="text-accent hover:underline"
                >
                  see my activity on GitHub
                </a>
                .
              </p>
            )}

            {status === 'empty' && <p className="text-muted">no commits found.</p>}

            {status === 'ready' && (
              <ul className="space-y-1">
                {commits.map((c) => (
                  <li key={`${c.repo}-${c.sha}`}>
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noreferrer"
                      className="group flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-2 py-1.5 -mx-2 rounded hover:bg-raised/70 transition-colors"
                    >
                      <span className="text-accent">{c.sha}</span>
                      <span className="text-text flex-1 min-w-[12ch] break-words group-hover:text-accent transition-colors">
                        {c.message}
                      </span>
                      <span className="text-accent2 text-[11px]">{c.repo}</span>
                      <span className="text-muted text-[11px] w-16 text-right shrink-0">
                        {timeAgo(c.date)}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="px-4 py-2 border-t border-border flex items-center justify-between text-[11px] font-mono text-muted">
            <span>live · api.github.com · cached 10 min</span>
            <a
              href={contactData.profiles.github}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 hover:text-accent transition-colors"
            >
              <Github size={12} />
              {OWNER}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
